import { useEffect } from 'react';

import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  AllowanceEnums,
  AllowancesNames,
  AllowanceTypesToHandle,
  ContractTypes,
  getHourlyRate,
  getRecurrence,
  pharmacyWorkshift,
  ProfessionalReplacementTypes,
  recurrenceType,
  TravelAllowanceEnums,
  updateWorkshift,
  updateWorkshiftContract,
} from '@pharmaplan/common';
import { goToFallback, handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';

import useGetChangedContract from '../LongTermContract/useGetChangedContract';
import { toJSDate } from '../../components/Dashboard/MainCalendar/helpers';
import { SelfUpdateWorkshiftValidator, SelfWorkshiftValidator } from '../../helpers/commonValidators';
import useUpdateContract from '../LongTermContract/useUpdateContract';
import { InvertedRepeatMap } from '../../helpers/Constants';
import { useAppDispatch } from '../useAppDispatch';
import { useAppSelector } from '../useAppSelector';
import {
  decimalConverterProxy,
  handlerForZeroes,
  parseFloatProxy,
} from '../../helpers/proxyHandlers';
import {
  makeReqParams,
} from '../../helpers/workshift/functions';
import useSyncMultiHours from './useSyncMultiHours';

const { Never } = recurrenceType;
const { meal, emergency, others, travel, accommodation } = AllowanceEnums;

const {
  travelAllowanceType,
  accommodationAllowanceType,
  paidHourlyRate: paidHourlyRateEnum,
  accommodationAllowance: accommodationAllowanceEnum,
  mealAllowance: mealAllowanceEnum,
  travelAllowanceAmount: travelAllowanceAmountEnum,
  travelAllowanceRatePerKm: travelAllowanceRatePerKmEnum,
  emergencyFees: emergencyFeesEnum,
  otherAllowance: otherAllowanceEnum,
  weekDayPaidHourlyRate: weekDayPaidHourlyEnum,
  weekendPaidHourlyRate: weekendPaidHourlyEnum,
} = AllowancesNames;

const useUpdateSelfShiftFormik = (
  id: string,
  isSeries: boolean,
  recurrenceId?: string,
) => {
  const dispatch = useAppDispatch();

  const { setAttributes, formatDisplayDate } = useUpdateContract();
  const currentWorkshift = useAppSelector(pharmacyWorkshift);

  const recurrenceSelector = useAppSelector(getRecurrence);
  const { getChangedItems, comparator } = useGetChangedContract();

  const { repeatEvery } = recurrenceSelector ?? {};

  const {
    recurrence,
    paidHourlyRate,
    notes,
    endDate,
    startDate,
    travelAllowanceRatePerKm,
    travelAllowanceAmount,
    mealAllowance,
    emergencyFees: currentEmergency,
    accommodationAllowance,
    accommodationAllowanceType: accommodationType,
    longTermShiftContract,
    travelAllowanceTypes,
    otherAllowance,
    longTermShiftContractId,
  } = currentWorkshift ?? {};

  const {
    startDate: longContractStart,
    endDate: longContractEnd,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
    allowanceNotes,
  } = longTermShiftContract ?? {};

  const { recurrenceType: currentRecurrenceType, endDate: recurrenceEndDate, startDate: recurrenceStartDate } = recurrence ?? {};

  const isLongTerm = !!longTermShiftContractId;
  const start = isSeries && recurrenceStartDate ? recurrenceStartDate : startDate;

  const valuesToParse = {
    travelAllowanceRatePerKm,
    mealAllowance,
    emergencyFees: currentEmergency,
    accommodationAllowance,
    travelAllowanceAmount,
    paidHourlyRate,
    otherAllowance,
    weekDayPaidHourlyRate,
    weekendPaidHourlyRate,
  };

  const valuesHandledForZero = new Proxy(valuesToParse, handlerForZeroes);
  const valuesHandledForDecimals = new Proxy(
    valuesHandledForZero,
    decimalConverterProxy,
  );

  const handleSubmit = (values: FormikValues) => {
    // NOTE: THE ORDER MATTERS
    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKmEnum],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmountEnum],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowanceEnum],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceType]
        === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowanceEnum],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFeesEnum],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowanceEnum],
        null,
      ),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    if (isLongTerm) {
      const { startDate: defaultStart, endDate: defaultEnd } = comparator();
      console.log({
        ...makeReqParams(values, recurrenceSelector, { startDate: defaultStart, endDate: defaultEnd }),
        paidHourlyRate: values[paidHourlyRateEnum],
        recurrenceId: isSeries ? recurrenceId || '' : null,
        workshiftId: id,
        shiftContractAttributes: getChangedItems(),
        typeReplacement:
          values?.replacementType ?? ProfessionalReplacementTypes.pharmacist,
        longTermContractId: longTermShiftContractId,
        [weekendPaidHourlyEnum]: parseFloat(values[weekendPaidHourlyEnum]),
        [weekDayPaidHourlyEnum]: parseFloat(values[weekDayPaidHourlyEnum]),
      });

      dispatch(
        updateWorkshiftContract({
          ...makeReqParams(values, recurrenceSelector, { startDate: defaultStart, endDate: defaultEnd }),
          workshiftId: id,
          shiftContractAttributes: getChangedItems(),
          typeReplacement:
            values?.replacementType ?? ProfessionalReplacementTypes.pharmacist,
          longTermContractId: longTermShiftContractId,
          weekendPaidHourlyRate: goToFallback(parseFloat(values[weekendPaidHourlyEnum]), 0),
          weekDayPaidHourlyRate: goToFallback(parseFloat(values[weekDayPaidHourlyEnum]), 0),
          allowanceNotes: values?.allowanceNotes,
        }),
      );
      return;
    }

    dispatch(
      updateWorkshift({
        ...makeReqParams(values, recurrenceSelector),
        travelAllowanceTypes: values[travelAllowanceType],
        accommodationAllowanceType: values[accommodationAllowanceType] || null,
        paidHourlyRate: values[paidHourlyRateEnum],
        recurrenceId: isSeries ? recurrenceId || '' : null,
        workshiftId: id,
        typeReplacement:
          values?.replacementType ?? ProfessionalReplacementTypes.pharmacist,
        ...handledForDecimals,
      }),
    );
  };

  const recurrenceTypeForWorkshift = isSeries
    ? currentRecurrenceType ?? Never
    : repeatEvery;

  let dates: Record<string, string | undefined> = {
    endDate: recurrenceEndDate || endDate,
  };

  if (isLongTerm) {
    dates = {
      endDate: longContractEnd,
      startDate: longContractStart,
    };
  }

  const formik = useFormik({
    initialValues: {
      ...currentWorkshift,
      ...valuesHandledForDecimals,
      ...dates,
      selectedDate: DateTime.fromISO(start, {
        setZone: true,
      }),
      allDay: false,
      contractType: isLongTerm
        ? ContractTypes.longTerm
        : ContractTypes.shortTerm,
      notes,
      weekdayHours: 0,
      weekendHours: 0,
      displayDate: formatDisplayDate(longContractStart, longContractEnd),
      startTime: toJSDate(startDate),
      endTime: toJSDate(endDate),
      recurrence: InvertedRepeatMap[recurrenceTypeForWorkshift],
      allowanceNotes,
      [accommodationAllowanceType]: `${accommodationType}`,
      [AllowancesNames.travelAllowanceType]:
        currentWorkshift?.travelAllowanceTypes?.filter(Boolean),
    },
    enableReinitialize: true,
    validationSchema: SelfUpdateWorkshiftValidator(),
    onSubmit: handleSubmit,
  });

  useEffect(() => {
    formik.setFieldValue(meal, !!mealAllowance);
    formik.setFieldValue(travel, !!travelAllowanceTypes?.length);
    formik.setFieldValue(accommodation, !!accommodationType);
    formik.setFieldValue(emergency, !!currentEmergency);
    formik.setFieldValue(others, !!otherAllowance);
    setAttributes(longContractStart, longContractEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkshift]);

  useSyncMultiHours(formik);

  useEffect(() => {
    dispatch(getHourlyRate());
  }, []);

  return { formik };
};

export default useUpdateSelfShiftFormik;
